/**
 * RETOBLOCK BLE FAST OTA MODAL (V2.0)
 * Giao diện nâng cấp Firmware Robot trực tiếp qua Bluetooth Low Energy (Direct Streaming).
 * Khóa nguồn nạp chính thức đã qua xác thực từ Cloud Supabase Storage với Dynamic Manifest Fetching.
 */

import { supabaseService } from '../core/supabase-client.js';
import { calculateCRC32 } from '../core/ble-protocol.js';

// Cấu hình dự phòng an toàn (Fallback) khi thiết bị ngoại tuyến hoặc chưa kết nối Cloud
const DEFAULT_MANIFEST = {
  target_kit: 'RETOLOGIX_Basic',
  version: 'v1.1.25',
  file_size: 755648,
  crc32: '62BD29B2',
  sha256: '710484baab89bbb99536aa886c7cf8da4fbc69b8e410800c406d36519d71ca6d',
  download_url: '',
  changelog: 'Tăng BLE Watchdog timeout lên 8000ms chống nghẽn tab nền và tối ưu hàng đợi Heartbeat',
  released_at: '2026-10-18T06:30:18.660Z'
};

export class OtaModal {
  constructor(options) {
    this.container = options.container;
    this.bleTransport = options.bleTransport;
    this.onCompleted = options.onCompleted;

    this.isOpen = false;
    this.isUpgrading = false;
    this.isFetchingManifest = false;
    this.currentManifest = { ...DEFAULT_MANIFEST };
    this.rebootTimer = null;

    this.init();
  }

  init() {
    this.container.innerHTML = `
      <div class="modal-overlay" id="ota-modal-overlay">
        <div class="modal-card ota-modal-card">
          <!-- Modal Header -->
          <div class="modal-header">
            <div class="modal-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1E56A0" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span>Nâng Cấp Firmware Robot (BLE Fast OTA)</span>
            </div>
            <button class="btn-close-modal" id="btn-close-ota-modal" title="Đóng">&times;</button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body ota-modal-body">
            <!-- 1. Version Comparison Card -->
            <div class="ota-version-card">
              <div class="ota-version-column">
                <span class="ota-ver-label">Phiên bản trên xe</span>
                <div class="ota-ver-badge current">
                  <span class="ota-dot-status"></span>
                  <span id="ota-current-ver">v1.0.0 (ota_0)</span>
                </div>
              </div>
              <div class="ota-arrow-separator">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E56A0" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </div>
              <div class="ota-version-column">
                <span class="ota-ver-label">Bản cập nhật mới</span>
                <div class="ota-ver-badge target">
                  <span class="ota-dot-pulse"></span>
                  <span id="ota-target-ver">v1.1.12 (Fast OTA)</span>
                </div>
              </div>
            </div>

            <!-- 2. Official Release Card (Verified Badge, Dynamic Manifest) -->
            <div class="ota-official-card" id="ota-official-card">
              <div class="ota-official-header">
                <div class="ota-verified-badge" title="Được biên dịch, ký mã và bảo chứng bởi máy chủ RETOBOTS">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
                  <span>Đã xác thực bởi RETOBOTS</span>
                </div>
                <span class="ota-recommended-tag">Bản phát hành chính thức (Được khuyến nghị)</span>
              </div>

              <div class="ota-official-title" id="ota-release-title">
                Bản phát hành chính thức v1.1.12
              </div>

              <div class="ota-official-meta-grid">
                <div class="ota-meta-item">
                  <span class="meta-label">Dung lượng</span>
                  <span class="meta-value" id="ota-release-size">~735 KB</span>
                </div>
                <div class="ota-meta-item">
                  <span class="meta-label">Thời gian nạp</span>
                  <span class="meta-value">~35 giây</span>
                </div>
                <div class="ota-meta-item">
                  <span class="meta-label">Mã CRC-32</span>
                  <span class="meta-value" id="ota-release-crc">0xA4B2C19E</span>
                </div>
              </div>

              <div class="ota-changelog-box">
                <div class="ota-changelog-title">Nội dung nâng cấp:</div>
                <p class="ota-changelog-text" id="ota-release-changelog">
                  Tối ưu DLE MTU 512B, nạp nhanh 35s, vá lỗi Watchdog Fail-Safe.
                </p>
              </div>
            </div>

            <!-- 3. Safety Notice Banner -->
            <div class="ota-notice-banner" id="ota-notice-banner">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E65100" stroke-width="2" style="flex-shrink: 0; margin-top: 1px;"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <div>
                <strong>Lưu ý an toàn:</strong> Quá trình nạp thực hiện 100% qua sóng Bluetooth, máy tính vẫn duy trì mạng Wi-Fi Internet bình thường. Tuyệt đối <strong>không tắt nguồn robot</strong> hoặc đóng trình duyệt khi đang nạp.
              </div>
            </div>

            <!-- Inline Error Banner (Non-blocking replacement for window.alert) -->
            <div class="ota-inline-error" id="ota-inline-error" style="display: none;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" stroke-width="2" style="flex-shrink: 0; margin-top: 1px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <div id="ota-inline-error-text"></div>
            </div>

            <!-- 4. Real-time Progress & Stats (Hidden until upgrade starts) -->
            <div class="ota-progress-section" id="ota-progress-section" style="display: none;">
              <div class="ota-progress-header">
                <span class="ota-status-badge" id="ota-status-badge">Đang nạp dữ liệu...</span>
                <span class="ota-progress-pct" id="ota-progress-pct">0%</span>
              </div>
              <div class="ota-progress-track">
                <div class="ota-progress-fill" id="ota-progress-fill" style="width: 0%;"></div>
              </div>

              <!-- Metrics Grid -->
              <div class="ota-stats-grid">
                <div class="ota-stat-card">
                  <span class="ota-stat-label">Vận tốc truyền</span>
                  <span class="ota-stat-val" id="ota-speed-val">0.0 KB/s</span>
                </div>
                <div class="ota-stat-card">
                  <span class="ota-stat-label">Thời gian còn lại</span>
                  <span class="ota-stat-val" id="ota-eta-val">--</span>
                </div>
                <div class="ota-stat-card">
                  <span class="ota-stat-label">Dung lượng</span>
                  <span class="ota-stat-val" id="ota-bytes-val">0 / 0 KB</span>
                </div>
                <div class="ota-stat-card">
                  <span class="ota-stat-label">Tiến trình khối</span>
                  <span class="ota-stat-val" id="ota-chunks-val">0 / 0</span>
                </div>
              </div>

              <div class="ota-log-msg" id="ota-log-msg">Sẵn sàng khởi động phiên stream...</div>
            </div>

            <!-- 5. Completion / Reboot Card (Hidden until done) -->
            <div class="ota-reboot-card" id="ota-reboot-card" style="display: none;">
              <div class="ota-success-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00C853" stroke-width="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div class="ota-reboot-title" id="ota-reboot-title">Nâng cấp Firmware thành công! Robot đang khởi động lại vào bản v1.1.12...</div>
              <div class="ota-reboot-desc" id="ota-reboot-countdown-text">
                Robot đang kích hoạt bản ROM mới và khởi động lại sau <strong>3</strong> giây...
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer ota-modal-footer">
            <button class="btn-secondary-modal" id="btn-cancel-ota" style="display: none;">Hủy Nâng Cấp</button>
            <button class="btn-secondary-modal" id="btn-close-ota-footer">Đóng</button>
            <button class="btn-primary-modal btn-ota-start" id="btn-start-ota">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span>Bắt Đầu Nâng Cấp</span>
            </button>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  bindEvents() {
    const btnClose = document.getElementById('btn-close-ota-modal');
    const btnCloseFooter = document.getElementById('btn-close-ota-footer');
    const btnStart = document.getElementById('btn-start-ota');
    const btnCancel = document.getElementById('btn-cancel-ota');

    // Close actions
    const handleClose = () => {
      if (this.isUpgrading) {
        if (!confirm('Quá trình nâng cấp firmware đang diễn ra! Bạn có chắc muốn hủy và đóng không?')) {
          return;
        }
        this.bleTransport.abortOta();
      }
      this.hide();
    };

    btnClose.addEventListener('click', handleClose);
    btnCloseFooter.addEventListener('click', handleClose);

    // Start OTA Action
    btnStart.addEventListener('click', () => this.handleStartUpgrade());

    // Cancel OTA Action
    btnCancel.addEventListener('click', () => {
      if (confirm('Bạn có chắc chắn muốn hủy phiên nạp Firmware OTA?')) {
        this.bleTransport.abortOta(0x02);
        this.setUpgradeState(false);
        const logMsg = document.getElementById('ota-log-msg');
        if (logMsg) logMsg.textContent = 'Đã hủy phiên nạp bởi người dùng.';
      }
    });
  }

  getManifestUrls() {
    const supabaseUrl = (supabaseService?.supabaseUrl || import.meta.env?.VITE_SUPABASE_URL || '').replace(/\/+$/, '');
    if (!supabaseUrl || supabaseUrl.includes('your-project-id')) {
      return [];
    }
    const timestamp = Date.now();
    return [
      `${supabaseUrl}/storage/v1/object/public/firmware-releases/retologix_basic/latest-manifest.json?t=${timestamp}`,
      `${supabaseUrl}/storage/v1/object/public/firmware-releases/retocar/latest-manifest.json?t=${timestamp}`
    ];
  }

  async fetchLatestManifest() {
    const manifestUrls = this.getManifestUrls();
    if (!manifestUrls || manifestUrls.length === 0) {
      this.applyManifest(DEFAULT_MANIFEST);
      return;
    }

    this.isFetchingManifest = true;
    let fetchedData = null;

    for (const url of manifestUrls) {
      try {
        const resp = await fetch(url, { cache: 'no-store' });
        if (resp.ok) {
          const data = await resp.json();
          if (data && data.version) {
            fetchedData = data;
            break;
          }
        }
      } catch (e) {
        // Continue to fallback url
      }
    }

    if (fetchedData) {
      this.currentManifest = {
        ...DEFAULT_MANIFEST,
        ...fetchedData
      };
      this.applyManifest(this.currentManifest);
    } else {
      console.warn('[BLE OTA] Không thể tải manifest từ Cloud, sử dụng cấu hình mặc định.');
      this.applyManifest(DEFAULT_MANIFEST);
    }
    this.isFetchingManifest = false;
  }

  applyManifest(manifest) {
    this.currentManifest = manifest;

    const targetVerEl = document.getElementById('ota-target-ver');
    const titleEl = document.getElementById('ota-release-title');
    const sizeEl = document.getElementById('ota-release-size');
    const crcEl = document.getElementById('ota-release-crc');
    const changelogEl = document.getElementById('ota-release-changelog');

    if (targetVerEl) {
      targetVerEl.textContent = `${manifest.version} (Fast OTA)`;
    }
    if (titleEl) {
      titleEl.textContent = `Bản phát hành chính thức ${manifest.version}`;
    }
    if (sizeEl) {
      const sizeKb = manifest.file_size ? (manifest.file_size / 1024).toFixed(1) : '733.5';
      sizeEl.textContent = `~${sizeKb} KB`;
    }
    if (crcEl) {
      const hexCrc = manifest.crc32 ? String(manifest.crc32).replace(/^0x/i, '').toUpperCase() : 'A4B2C19E';
      crcEl.textContent = `0x${hexCrc}`;
    }
    if (changelogEl) {
      changelogEl.textContent = manifest.changelog || 'Tối ưu DLE MTU 512B, nạp nhanh ~35s, vá lỗi Watchdog Fail-Safe.';
    }
  }

  updateCurrentVersion(version) {
    if (!version) return;
    this.deviceVersion = version;
    const formattedVer = version.startsWith('v') ? version : `v${version}`;
    const curVerEl = document.getElementById('ota-current-ver');
    if (curVerEl) {
      curVerEl.textContent = `${formattedVer} (Active Slot)`;
    }
  }

  show() {
    this.isOpen = true;
    const overlay = document.getElementById('ota-modal-overlay');
    if (overlay) overlay.classList.add('active');
    this.resetView();

    // Hiển thị phiên bản thực tế đọc được từ Telemetry của xe thay vì mặc định v1.0.0
    if (this.bleTransport?.firmwareVersion) {
      this.updateCurrentVersion(this.bleTransport.firmwareVersion);
    } else if (this.deviceVersion) {
      this.updateCurrentVersion(this.deviceVersion);
    }

    this.fetchLatestManifest();
  }

  hide() {
    this.isOpen = false;
    const overlay = document.getElementById('ota-modal-overlay');
    if (overlay) overlay.classList.remove('active');
    if (this.rebootTimer) {
      clearInterval(this.rebootTimer);
      this.rebootTimer = null;
    }
  }

  showInlineError(msg) {
    const errorBox = document.getElementById('ota-inline-error');
    const errorText = document.getElementById('ota-inline-error-text');
    if (errorBox && errorText) {
      errorText.textContent = msg;
      errorBox.style.display = 'flex';
    }
  }

  clearInlineError() {
    const errorBox = document.getElementById('ota-inline-error');
    if (errorBox) {
      errorBox.style.display = 'none';
    }
  }

  resetView() {
    this.isUpgrading = false;
    this.clearInlineError();
    document.getElementById('ota-progress-section').style.display = 'none';
    document.getElementById('ota-reboot-card').style.display = 'none';
    document.getElementById('ota-notice-banner').style.display = 'flex';
    document.getElementById('ota-official-card').style.display = 'flex';
    document.getElementById('btn-start-ota').style.display = 'inline-flex';
    document.getElementById('btn-cancel-ota').style.display = 'none';
    document.getElementById('btn-close-ota-footer').style.display = 'inline-flex';
    document.getElementById('ota-progress-fill').style.width = '0%';
    document.getElementById('ota-progress-pct').textContent = '0%';
    document.getElementById('ota-speed-val').textContent = '0.0 KB/s';
    document.getElementById('ota-eta-val').textContent = '--';
  }

  setUpgradeState(upgrading) {
    this.isUpgrading = upgrading;
    const btnStart = document.getElementById('btn-start-ota');
    const btnCancel = document.getElementById('btn-cancel-ota');
    const btnCloseFooter = document.getElementById('btn-close-ota-footer');
    const btnClose = document.getElementById('btn-close-ota-modal');
    const progressSec = document.getElementById('ota-progress-section');
    const noticeBanner = document.getElementById('ota-notice-banner');
    const officialCard = document.getElementById('ota-official-card');

    if (upgrading) {
      this.clearInlineError();
      btnStart.style.display = 'none';
      btnCancel.style.display = 'inline-flex';
      btnCloseFooter.style.display = 'none';
      btnClose.style.opacity = '0.5';
      noticeBanner.style.display = 'none';
      officialCard.style.display = 'none';
      progressSec.style.display = 'flex';
    } else {
      btnStart.style.display = 'inline-flex';
      btnCancel.style.display = 'none';
      btnCloseFooter.style.display = 'inline-flex';
      btnClose.style.opacity = '1';
    }
  }

  async handleStartUpgrade() {
    this.clearInlineError();

    if (!this.bleTransport.isConnected) {
      this.showInlineError('Vui lòng kết nối robot qua Bluetooth trước khi nâng cấp firmware!');
      return;
    }

    const btnStart = document.getElementById('btn-start-ota');
    btnStart.disabled = true;

    let binaryData;
    const downloadUrl = this.currentManifest?.download_url;

    try {
      if (downloadUrl && downloadUrl.startsWith('http')) {
        // Tải trực tiếp binary từ Supabase Storage Cloud
        const resp = await fetch(downloadUrl);
        if (!resp.ok) {
          throw new Error(`Không thể tải gói firmware từ Cloud (HTTP ${resp.status})`);
        }
        const arrayBuffer = await resp.arrayBuffer();
        binaryData = new Uint8Array(arrayBuffer);

        // Đối chiếu mã toàn vẹn CRC-32 với manifest
        if (this.currentManifest?.crc32) {
          const calcCrc = calculateCRC32(binaryData);
          const calcHex = calcCrc.toString(16).toUpperCase().padStart(8, '0');
          const expHex = String(this.currentManifest.crc32).replace(/^0x/i, '').toUpperCase().padStart(8, '0');
          if (calcHex !== expHex) {
            throw new Error(`Mã kiểm tra toàn vẹn CRC-32 không khớp! (Thực tế: 0x${calcHex}, Kỳ vọng: 0x${expHex})`);
          }
        }
      } else {
        // Môi trường thử nghiệm ngoại tuyến / Fallback nhị phân chuẩn ESP32
        binaryData = this.generateSampleFirmwareBinary();
      }
    } catch (fetchErr) {
      btnStart.disabled = false;
      this.showInlineError(`Lỗi chuẩn bị gói Firmware: ${fetchErr.message}`);
      return;
    }

    btnStart.disabled = false;
    this.setUpgradeState(true);

    const statusBadge = document.getElementById('ota-status-badge');
    const progressFill = document.getElementById('ota-progress-fill');
    const progressPct = document.getElementById('ota-progress-pct');
    const speedVal = document.getElementById('ota-speed-val');
    const etaVal = document.getElementById('ota-eta-val');
    const bytesVal = document.getElementById('ota-bytes-val');
    const chunksVal = document.getElementById('ota-chunks-val');
    const logMsg = document.getElementById('ota-log-msg');

    statusBadge.textContent = 'Đang bắt tay BLE Fast OTA...';
    logMsg.textContent = 'Gửi gói 0x30 mở phiên Inactive Slot Flash...';

    try {
      await this.bleTransport.startFastOta(binaryData, this.currentManifest?.crc32, (prog) => {
        statusBadge.textContent = 'Đang truyền dữ liệu nhị phân (Write Without Response)...';
        progressFill.style.width = `${prog.percent}%`;
        progressPct.textContent = `${prog.percent}%`;
        speedVal.textContent = `${prog.speedKbps} KB/s`;
        etaVal.textContent = prog.etaSeconds > 0 ? `~${prog.etaSeconds}s` : 'Hoàn tất';
        const ackText = (prog.confirmedChunk && prog.confirmedChunk > 0)
          ? ` [Robot đã lưu an toàn: #${prog.confirmedChunk}]`
          : '';
        logMsg.textContent = `Đang nạp khối #${prog.chunkIdx} / ${prog.totalChunks} (${Math.round(prog.bytesSent / 1024)} KB)${ackText}...`;
      });

      // Hoàn tất nạp thành công
      this.isUpgrading = false;
      const targetVersion = this.currentManifest?.version || 'v1.1.12';
      const successMessage = `Nâng cấp Firmware thành công! Robot đang khởi động lại vào bản ${targetVersion}...`;
      statusBadge.textContent = successMessage;
      document.getElementById('ota-progress-section').style.display = 'none';
      document.getElementById('btn-cancel-ota').style.display = 'none';
      document.getElementById('btn-close-ota-footer').style.display = 'inline-flex';

      const rebootCard = document.getElementById('ota-reboot-card');
      const rebootTitle = document.getElementById('ota-reboot-title');
      if (rebootTitle) {
        rebootTitle.textContent = successMessage;
      }
      rebootCard.style.display = 'block';

      // Đếm lùi khởi động lại
      let countdown = 3;
      const countdownText = document.getElementById('ota-reboot-countdown-text');

      this.rebootTimer = setInterval(() => {
        countdown--;
        if (countdown > 0) {
          countdownText.innerHTML = `Robot đang kích hoạt bản ROM mới và khởi động lại sau <strong>${countdown}</strong> giây...`;
        } else {
          clearInterval(this.rebootTimer);
          this.rebootTimer = null;
          countdownText.innerHTML = `<span style="color: #00C853; font-weight: 700;">Robot đã khởi động vào Firmware ${targetVersion}! Sẵn sàng kết nối lại.</span>`;
          const curVerEl = document.getElementById('ota-current-ver');
          if (curVerEl) {
            curVerEl.textContent = `${targetVersion} (Active Slot)`;
          }
          if (this.onCompleted) {
            this.onCompleted(targetVersion);
          }
        }
      }, 1000);
    } catch (err) {
      this.setUpgradeState(false);
      statusBadge.textContent = 'Nâng cấp thất bại';
      logMsg.textContent = `Lỗi: ${err.message}`;
      this.showInlineError(`Nâng cấp Firmware thất bại: ${err.message}`);
    }
  }

  generateSampleFirmwareBinary() {
    // Tạo mảng binary 751KB giả lập chuẩn với ESP image magic byte 0xE9
    const size = 751136;
    const buffer = new Uint8Array(size);

    // ESP32 Image Header Magic
    buffer[0] = 0xE9; // Magic Byte
    buffer[1] = 0x03; // Segments count
    buffer[2] = 0x02; // SPI Flash mode DIO
    buffer[3] = 0x20; // SPI Flash size 4MB, 40MHz

    // Điền dữ liệu mô phỏng
    for (let i = 4; i < size; i++) {
      buffer[i] = (i ^ (i >> 3)) & 0xFF;
    }

    return buffer;
  }
}
